import React, { useState } from 'react';
import { X, Loader2, ImageIcon, LayoutTemplate, Sparkles, BookOpen, PenLine, Target, Pencil, BarChart2, Droplets, Layers, Palette, Star, Film, Building2, FileText } from 'lucide-react';
import type { AspectRatio } from '../services/imageGen';
import { ASPECT_RATIO_LABELS, ASPECT_RATIO_SIZES } from '../services/imageGen';

export interface GenerateImageParams {
  prompt: string;
  promptType: string;
  aspectRatio: AspectRatio;
  imageStyle?: string;
}

interface GenerateImageModalProps {
  onGenerate: (params: GenerateImageParams) => void;
  onClose: () => void;
  loading: boolean;
  countdown: number;
  taskSourceMode?: boolean;
  taskName?: string;
  sourceImagePreviewUrl?: string;
}

const CUSTOM_ID = 'custom';

const PRESET_TYPES: { id: string; label: string; icon: React.ReactNode; prompt: string }[] = [
  {
    id: 'challenge-summary',
    label: 'Challenge Summary',
    icon: <LayoutTemplate size={18} />,
    prompt: 'Create a visual summary of the current challenge landscape. Show the key pain points, obstacles, and frustrations faced by the user personas. Include representative people, data barriers, budget constraints, and the emotional weight of the problem. Make it clear, empathetic and compelling.',
  },
  {
    id: 'future-state',
    label: 'Future State',
    icon: <Sparkles size={18} />,
    prompt: 'Create an inspiring vision of the future state. Show empowered users with access to the solution, happy outcomes, data flowing freely, and the transformation from the current struggle. Make it optimistic, modern and human-centred.',
  },
  {
    id: 'workbook-cover',
    label: 'Workbook Cover',
    icon: <BookOpen size={18} />,
    prompt: 'Create a professional workshop workbook cover image. It should feel polished and purposeful, incorporating themes from the session context with a confident visual identity suitable for a printed or digital workbook.',
  },
  {
    id: 'workshop-goal',
    label: 'Workshop Goal',
    icon: <Target size={18} />,
    prompt: 'Create a compelling visual that captures the core goal of this workshop. Show the desired outcome, the transformation being pursued, and the people involved. Make it purposeful, energising, and aligned with the session intent.',
  },
  {
    id: CUSTOM_ID,
    label: 'Custom',
    icon: <PenLine size={18} />,
    prompt: '',
  },
];

const STYLE_OPTIONS: { id: string; label: string; icon: React.ReactNode; prompt: string }[] = [
  {
    id: 'doodle',
    label: 'Doodle Art',
    icon: <Pencil size={16} />,
    prompt: 'Bold hand-drawn doodle illustration. Black ink outlines, flat colour fills, whimsical characters, clean playful composition, marker-pen aesthetic, approachable and expressive.',
  },
  {
    id: 'sketchnote',
    label: 'Sketchnote',
    icon: <PenLine size={16} />,
    prompt: 'Hand-drawn ink outlines with vibrant digital watercolour colouring. Optimistic palette, expressive line work, playful architectural detail, crisp black pen strokes, subtle crosshatching, colourful highlights, professional infographic finish. Magazine-quality illustration.',
  },
  {
    id: 'civic-sketch',
    label: 'Civic Sketch',
    icon: <Building2 size={16} />,
    prompt: 'Detailed urban sketchbook illustration for public sector storytelling. Hand-drawn ink, digital watercolour, vibrant civic palette, approachable characters, architectural detail, editorial infographic aesthetic. Optimistic community-focused mood.',
  },
  {
    id: 'infographic',
    label: 'Infographic',
    icon: <BarChart2 size={16} />,
    prompt: 'Clean flat-design infographic illustration. Bold icons, modern colour blocks, clear visual hierarchy, minimal characters, professional data-forward aesthetic. Suitable for printed reports and executive presentations.',
  },
  {
    id: 'watercolour',
    label: 'Watercolour',
    icon: <Droplets size={16} />,
    prompt: 'Loose watercolour painting. Soft brushstrokes, gentle colour blending, warm light, painterly texture. Artistic and human-centred, warm and emotive.',
  },
  {
    id: 'isometric',
    label: 'Isometric',
    icon: <Layers size={16} />,
    prompt: 'Clean isometric vector illustration. 3D geometric shapes, flat colour with subtle depth, modern tech aesthetic, structured and precise. Ideal for systems, processes and environments.',
  },
  {
    id: 'impressionist',
    label: 'Impressionist',
    icon: <Palette size={16} />,
    prompt: 'Rich impressionist oil painting. Visible impasto brushstrokes, warm luminous light, painterly colour mixing, romantic and expressive atmosphere. Evocative of Monet or Pissarro. Emotive and artistic.',
  },
  {
    id: 'retro-poster',
    label: 'Retro Poster',
    icon: <Star size={16} />,
    prompt: 'Bold mid-century poster illustration. Limited flat colour palette, strong geometric shapes, clean graphic design, optimistic constructivist mood. Inspired by 1950s–60s civic and travel poster art. Striking and timeless.',
  },
  {
    id: 'cinematic',
    label: 'Cinematic',
    icon: <Film size={16} />,
    prompt: 'Cinematic digital render with photorealistic lighting. Rich depth of field, dramatic natural light, highly detailed environments and characters. Serious, impactful, and immersive. Suitable for emotionally resonant challenge or future-state imagery.',
  },
];

const ASPECT_RATIOS: AspectRatio[] = ['widescreen', 'widescreen-portrait', 'a4-landscape', 'a4', 'square'];

const RATIO_ICONS: Record<AspectRatio, string> = {
  widescreen: '▬',
  'widescreen-portrait': '▮',
  a4: '▮',
  'a4-landscape': '▬',
  square: '■',
};

function getPromptLabel(taskSourceMode: boolean, hasSourceImage: boolean, isCustom: boolean): React.ReactNode {
  const opt = <span className="font-normal normal-case">(optional)</span>;
  if (taskSourceMode && hasSourceImage) return 'Prompt';
  if (taskSourceMode) return <>Additional instructions {opt}</>;
  if (isCustom) return 'Prompt';
  return <>Additional instructions {opt}</>;
}

function getPromptPlaceholder(taskSourceMode: boolean, hasSourceImage: boolean, isCustom: boolean): string {
  if (taskSourceMode && hasSourceImage) return 'Describe what to change, add, or create based on this image…';
  if (taskSourceMode) return 'Add specific style guidance, colours, or extra detail to steer the image…';
  if (isCustom) return 'Describe exactly what you want to generate…';
  return 'Add specific context, names, colours, or extra detail…';
}

function getPromptRows(taskSourceMode: boolean, isCustom: boolean): number {
  if (taskSourceMode) return 4;
  if (isCustom) return 5;
  return 3;
}

const TaskSourceBanner: React.FC<{ sourceImagePreviewUrl?: string; taskName?: string }> = ({ sourceImagePreviewUrl, taskName }) => {
  const taskLabel = taskName ? '"' + taskName + '"' : 'Task output';
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-primary/5 border border-primary/20 rounded-xl">
      {sourceImagePreviewUrl ? (
        <img src={sourceImagePreviewUrl} alt="Source" className="w-16 h-12 object-cover rounded-lg border border-primary/20 shrink-0" />
      ) : (
        <FileText size={16} className="text-primary shrink-0" />
      )}
      <div>
        <p className="text-xs font-semibold text-primary">
          {sourceImagePreviewUrl ? 'Editing from source image' : 'Prompt from task output'}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          {sourceImagePreviewUrl
            ? 'Describe what to change, add, or emphasise in the prompt below.'
            : `${taskLabel} will be summarised by AI into an image prompt.`}
        </p>
      </div>
    </div>
  );
};

const PresetPicker: React.FC<{
  selectedPreset: string;
  onSelect: (id: string) => void;
  onSetAspectRatio: (r: AspectRatio) => void;
  onClearPrompt: () => void;
}> = ({ selectedPreset, onSelect, onSetAspectRatio, onClearPrompt }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Content type</label>
    <div className="grid grid-cols-5 gap-2">
      {PRESET_TYPES.map(preset => (
        <button
          key={preset.id}
          onClick={() => {
            onSelect(preset.id);
            if (preset.id === 'workbook-cover') onSetAspectRatio('a4');
            if (preset.id !== CUSTOM_ID) onClearPrompt();
          }}
          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all ${
            selectedPreset === preset.id ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'
          }`}
        >
          {preset.icon}
          <span className="text-xs font-semibold leading-tight">{preset.label}</span>
        </button>
      ))}
    </div>
  </div>
);

const AspectRatioPicker: React.FC<{ aspectRatio: AspectRatio; onChange: (r: AspectRatio) => void }> = ({ aspectRatio, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Aspect ratio</label>
    <div className="grid grid-cols-5 gap-2">
      {ASPECT_RATIOS.map(ratio => {
        const active = aspectRatio === ratio;
        return (
          <button key={ratio} onClick={() => onChange(ratio)}
            className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 transition-all ${active ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
            <span className="text-base leading-none">{RATIO_ICONS[ratio]}</span>
            <span className="text-[10px] font-semibold leading-tight text-center">{ASPECT_RATIO_LABELS[ratio]}</span>
            <span className="text-[9px] opacity-60 leading-none">{ASPECT_RATIO_SIZES[ratio]}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const StylePicker: React.FC<{ selectedStyle: string | null; onChange: (id: string | null) => void }> = ({ selectedStyle, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
      Style <span className="font-normal normal-case">(optional)</span>
    </label>
    <div className="grid grid-cols-3 gap-2">
      {STYLE_OPTIONS.map(style => {
        const active = selectedStyle === style.id;
        return (
          <button key={style.id} onClick={() => onChange(active ? null : style.id)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-left transition-all ${active ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
            {style.icon}
            <span className="text-xs font-semibold leading-tight">{style.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

export const GenerateImageModal: React.FC<GenerateImageModalProps> = ({
  onGenerate,
  onClose,
  loading,
  countdown,
  taskSourceMode = false,
  taskName,
  sourceImagePreviewUrl,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('challenge-summary');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('widescreen');
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);

  const isCustom = selectedPreset === CUSTOM_ID;
  const hasSourceImage = !!sourceImagePreviewUrl;
  const trimmedPrompt = customPrompt.trim();
  const isGenerateDisabled =
    loading ||
    (!taskSourceMode && isCustom && !trimmedPrompt) ||
    (taskSourceMode && hasSourceImage && !trimmedPrompt);

  const handleGenerate = () => {
    const styleOption = STYLE_OPTIONS.find(s => s.id === selectedStyle);
    const imageStyle = styleOption ? styleOption.prompt : undefined;
    if (taskSourceMode) {
      onGenerate({ prompt: trimmedPrompt, promptType: 'task-output', aspectRatio, imageStyle });
      return;
    }
    if (isCustom) {
      onGenerate({ prompt: trimmedPrompt, promptType: CUSTOM_ID, aspectRatio, imageStyle });
      return;
    }
    const preset = PRESET_TYPES.find(p => p.id === selectedPreset);
    const basePrompt = preset ? preset.prompt : '';
    const fullPrompt = trimmedPrompt ? `${basePrompt}\n\nAdditional context: ${trimmedPrompt}` : basePrompt;
    onGenerate({ prompt: fullPrompt, promptType: selectedPreset, aspectRatio, imageStyle });
  };

  const promptLabel = getPromptLabel(taskSourceMode, hasSourceImage, isCustom);
  const promptPlaceholder = getPromptPlaceholder(taskSourceMode, hasSourceImage, isCustom);
  const promptRows = getPromptRows(taskSourceMode, isCustom);
  const generatingLabel = countdown > 0 ? `… ${countdown}s` : '…';

  return (
    <div className="fixed inset-0 z-[1002] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-primary" />
            <span className="font-bold text-slate-800">Generate Image</span>
          </div>
          <button onClick={onClose} disabled={loading}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-40">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-5">
          {taskSourceMode
            ? <TaskSourceBanner sourceImagePreviewUrl={sourceImagePreviewUrl} taskName={taskName} />
            : <PresetPicker selectedPreset={selectedPreset} onSelect={setSelectedPreset} onSetAspectRatio={setAspectRatio} onClearPrompt={() => setCustomPrompt('')} />
          }

          <AspectRatioPicker aspectRatio={aspectRatio} onChange={setAspectRatio} />
          <StylePicker selectedStyle={selectedStyle} onChange={setSelectedStyle} />

          {/* Prompt */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {promptLabel}
            </label>
            <textarea
              value={customPrompt}
              onChange={e => setCustomPrompt(e.target.value)}
              placeholder={promptPlaceholder}
              rows={promptRows}
              disabled={loading}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none disabled:opacity-50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onClose} disabled={loading}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button onClick={handleGenerate} disabled={isGenerateDisabled}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50">
            {loading ? (
              <><Loader2 size={14} className="animate-spin" />Generating{generatingLabel}</>
            ) : (
              <><ImageIcon size={14} />Generate Image</>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default GenerateImageModal;
